// LiveStreamTopLeaderboard.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

import LiveStreamUserAvatar from '../container/LiveStreamUserAvatar.js';
import { ButtonId } from '../resource/trackingConstants.js';
import { color } from '../style/variables.js';
import {
  TOP_LEADERBOARD_USER_LEAST,
  liveStreamSessionLeaderboardCategory,
} from '../resource/liveStreamConstants.js';

const AVATAR_SIZE = 18;

export class LiveStreamTopLeaderboard extends React.PureComponent {
  state = {
    isIntersected: false,
  };
  fetchLeaderboard = () => {
    const { streamId, sessionId, category, fetchLiveStreamSessionLeaderboard } =
      this.props;
    const { isIntersected } = this.state;
    if (!isIntersected || !sessionId) {
      return;
    }
    return fetchLiveStreamSessionLeaderboard({
      streamId,
      sessionId,
      category,
      page: 1,
      limit: TOP_LEADERBOARD_USER_LEAST,
    });
  };
  componentDidMount() {
    this.fetchLeaderboard();
  }
  componentDidUpdate(prevProps, prevState) {
    const { sessionId } = this.props;
    const { isIntersected } = this.state;
    if (
      sessionId !== prevProps.sessionId ||
      isIntersected !== prevState.isIntersected
    ) {
      this.fetchLeaderboard();
    }
  }
  openGiftLeaderboard = () => {
    const { openModal } = this.props;
    openModal({ id: 'LiveStreamGiftLeaderboard' });
  };

  handleOnMouseEnter = () => {
    const { setLiveStreamPanel } = this.props;
    setLiveStreamPanel({ isTopLeaderboardScrolling: true });
  };

  handleOnMouseLeave = () => {
    const { setLiveStreamPanel } = this.props;
    setLiveStreamPanel({ isTopLeaderboardScrolling: false });
  };
  handleTouchStart = () => {
    const { setLiveStreamPanel } = this.props;
    setLiveStreamPanel({ isTopLeaderboardScrolling: true });
  };

  handleTouchEnd = () => {
    const { setLiveStreamPanel } = this.props;
    setLiveStreamPanel({ isTopLeaderboardScrolling: false });
  };
  render() {
    const { userIds, streamId } = this.props;
    if (userIds === null) {
      return null;
    }

    return (
      <ObservableLeaderboardWrapper
        onChange={inView => {
          if (inView) {
            this.setState({ isIntersected: true });
          }
        }}
      >
        {ref => (
          <StyledLiveStreamTopLeaderboard
            ref={ref}
            onMouseEnter={this.handleOnMouseEnter}
            onMouseLeave={this.handleOnMouseLeave}
            onTouchStart={this.handleTouchStart}
            onTouchEnd={this.handleTouchEnd}
          >
            {userIds.map((userId, index) => {
              return (
                <LeaderboardUser
                  data-element_id={
                    ButtonId.LiveStream.ButtonGiftLeaderboardTop10
                  }
                  key={userId ? userId : index}
                  onClick={this.openGiftLeaderboard}
                  rank={index + 1}
                >
                  <UserAvatarContainer>
                    <LiveStreamUserAvatar
                      streamId={streamId}
                      userId={userId}
                      badgeSize={28}
                      avatarSize={AVATAR_SIZE}
                      fontSize={14}
                      diamondSize={8}
                      defaultRank={index + 1}
                      shouldDisplayMVP={false}
                    />
                  </UserAvatarContainer>
                  {index > 2 && <UserAvatarPlacehoderBorder />}
                </LeaderboardUser>
              );
            })}
          </StyledLiveStreamTopLeaderboard>
        )}
      </ObservableLeaderboardWrapper>
    );
  }
}

LiveStreamTopLeaderboard.propTypes = {
  userIds: PropTypes.array,
  openModal: PropTypes.func,
  setLiveStreamPanel: PropTypes.func,
  fetchLiveStreamSessionLeaderboard: PropTypes.func,
  streamId: PropTypes.string,
  sessionId: PropTypes.string,
  category: PropTypes.string,
};

LiveStreamTopLeaderboard.defaultProps = {
  userIds: null,
  openModal: () => null,
  setLiveStreamPanel: () => null,
  fetchLiveStreamSessionLeaderboard: () => null,
  streamId: '',
  sessionId: '',
  category: liveStreamSessionLeaderboardCategory.GIFTS,
};

const ObservableLeaderboardWrapper = ({ onChange, children }) => {
  const handleInViewChange = React.useCallback(
    inView => {
      onChange(inView);
    },
    [onChange]
  );

  const inViewOptions = React.useMemo(
    () => ({
      triggerOnce: true,
      onChange: handleInViewChange,
    }),
    [handleInViewChange]
  );

  const { ref } = useInView(inViewOptions);
  return children(ref);
};

ObservableLeaderboardWrapper.propTypes = {
  onChange: PropTypes.func.isRequired,
  children: PropTypes.func.isRequired,
};

const StyledLiveStreamTopLeaderboard = styled.div`
  display: flex;
  align-items: center;
  max-width: 100%;
  height: auto;
  overflow-x: auto;
  overflow-y: hidden;
  cursor: pointer;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const LeaderboardUser = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 28px;
  height: 28px;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 36px 36px;
  margin-right: 2px;
  &:last-child {
    margin-right: 0;
  }
`;

const UserAvatarContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UserAvatarPlacehoderBorder = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: ${AVATAR_SIZE + 2}px;
  height: ${AVATAR_SIZE + 2}px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: solid 1px ${color.white};
`;

export default LiveStreamTopLeaderboard;
